import React, {  useState, useEffect } from 'react';
import { TableContainer, TableBody } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Helmet } from 'react-helmet';
import MuiTableHeader from '../../uiComponents/MuiTable/MuiTableHeader.js';
import MuiTablePagination from '../../uiComponents/MuiTable/MuiTablePagination.js';
import { stableSort, getComparator } from '../../uiComponents/MuiTable/MuiHelper.js';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../uiComponents/MuiTable/StyledTable.js';
import APIUtils from '../../utils/APIUtils.js';
import Button from '../../uiComponents/Button.js';
import Loading from '../Loading';
import ConfirmationModal from '../../uiComponents/ConfirmationModal.js';
import SuccessMessageModal from '../SuccessMessageModal';
import SessionConstants from '../../config/SessionConstants.js';
import AddNewUserModal from './AddNewUserModal';
import TableFilterConfig from '../../uiComponents/MuiTable/TableFilterConfig.js';
import BreadCrumbs from '../BreadCrumbs';
import './index.css';


//#region create columns header array
const columns = [
  { label: 'User Email', id: 'email', numeric: false, disablePadding: false },
  { label: 'Name', id: 'fullName', numeric: false, disablePadding: false },
  { label: 'NBS Program ID', id: 'programId', numeric: false, disablePadding: false },
  { label: 'User Role', id: 'roleName', numeric: false, disablePadding: false },
  { label: 'Status', id: 'isActive', numeric: false, disablePadding: false },
  { label: 'Actions', id: 'actions', numeric: true, disablePadding: false, align: 'center', type: 'blank' },
];
let  filterConfig=[
                 { type: 'email', label: 'User Email', field: 'email' },
                 { type: 'name', label: 'Name', field: 'fullName' },
                 { type: 'role', label: 'User Role', field: 'roleName' },
               ];
//#endregion


const UserManagement = () => {
  const [rows,setRows] = useState([]);  //hold original dataset
  const [tableData, setTableData] = useState([]);
  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [userPendingDelete, setUserPendingDelete] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState([]);
  const [isSysAdmin, setIsSysAdmin] = useState(sessionStorage.getItem(SessionConstants.IS_SYSADMIN) === 'true');
  const [defaultProgramId, setDefaultProgramId] = useState(sessionStorage.getItem(SessionConstants.PROGRAM_ID));
  const [warningMessage, setWarningMessage] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');
  //props on sort table
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('email');
  //prop for paging
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const loadUsers = async () => {
    // include inactive users in the response so that sysadmin and program managers can manage user status
    let users = isSysAdmin 
                 ? await APIUtils.getAllUsers(false)
                 : await APIUtils.getProgramUsers(defaultProgramId, false);   
    // need to filter out SYSADMIN role for managers since they shouldn't see or manage sysadmin users, but we want to keep the role info for any sysadmin users that managers do see (e.g. if a manager is also a sysadmin, or if there are sysadmin users in their program that they need to manage)
    if (!isSysAdmin){
        users = users.filter(user => user.roleName !== 'SYSADMIN');
       
    }
    setTableData(users);
    setRows(users);
     setLoading(false);
  }
  
 const loadProgramsAndRoles = async () => {
   let [programResponse, roleResponse] = await Promise.all([
        APIUtils.lookupPrograms(),
        APIUtils.lookupRoles(),
      ]);

      if (!isSysAdmin){
        roleResponse = roleResponse.filter(role => role.roleName != 'SYSADMIN');
        programResponse= programResponse.filter(program => program.shortName == defaultProgramId);
      }
      else{
        // only show program filter for sysadmin
        filterConfig.push({ type: 'program', label: 'Program(s)', field: 'programId' });
      }
      setPrograms(programResponse);
      setRoles(roleResponse);
    }
  useEffect(() => {
    setBreadCrumbs([{ name: 'UserManagement' }]);
    setLoading(true);
    loadUsers();
    loadProgramsAndRoles();
   
  }, []);


  //#region Sorting 
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  //#endregion

  //#region Paging
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAddModal = () => {
    setSelectedUser(null);
    setModalIsOpen(true);
  };

  const openModifyModal = (user) => {
    setSelectedUser(user);
    setModalIsOpen(true);
  };

  const closeUserModal = () => {
    setSelectedUser(null);
    setModalIsOpen(false);
  };

  const closeConfirmationModal = () => {
    setConfirmationMessage('');
    setWarningMessage('');
    setConfirmationModalIsOpen(false);
  };
  const openUpdateStatusModal = (user) => {
    if (user.isActive) {
        setConfirmationMessage(`Do you want to delete user ${user.fullName}?`); // reset any previous warning messages
        if (user.email == sessionStorage.getItem(SessionConstants.USER_EMAIL)) {
          setWarningMessage('WARNING: this would delete your current user entry from the user database, which can cause access issues and is not recommended!');
        }
        else {
          // verify if this is the last PROGRM MANAGER in the program, and if so, show a warning message since deleting the user would remove all program management access for that program
          const programManagers = rows.filter(u => u.programId == user.programId && u.roleName == 'PROGRAMADMIN');  
          if (user.roleName == 'PROGRAMADMIN' && programManagers.length == 1) { 
            setWarningMessage("WARNING: deleting this user would leave no user with the PROGRAMADMIN for this program and is not recommended!");
          }
        }
   }
   else{
       setConfirmationMessage(`Do you want to reactivate user ${user.fullName}?`); 
   }
    setSelectedUser(user);
    setConfirmationModalIsOpen(true);
  };

  const handleSaveUser = async (user, isModify) => {
    try {
          const roleName = roles.find(role => `${role.id}` === `${user.roleId}`)?.roleName;
      if (isModify) {
        await APIUtils.updateUser(user);
        setSuccessMessage([`Updated user ${user.email} successfully.`]);
      } else {
        await APIUtils.createUser(user);
        setSuccessMessage([`Created user ${user.email} successfully.`]);
      }
      setSuccessMessage(prevItems => [...prevItems, 
                                      `User: ${user.firstName} ${user.lastName}`,
                                      `Program: ${user.programId}`,
                                      `Role: ${roleName}`,])
      closeUserModal();
      await loadUsers();
      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error('Unable to save user:', error);
      setSuccessMessage(['Unable to save user. Please try again.', error.response?.data]);
      closeUserModal();
      setIsSuccessModalOpen(true);
    }
  }

  const updateUserStatus = async () => {
    try {
      await APIUtils.updateUserStatus(selectedUser.email, !selectedUser.isActive);
      var message =  (selectedUser.isActive)? `Deleted user ${selectedUser.email} successfully.` : `Reactivated user ${selectedUser.email} successfully.`;
      setSuccessMessage([message]); 
      closeConfirmationModal();
      await loadUsers();
      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error('Unable to update user status:', error);
      setSuccessMessage(['Unable to update user status. Please try again.', error.response?.data]);
      closeConfirmationModal();
      setIsSuccessModalOpen(true);
    }
  };

  return (
    <div className="pageMinHeight">
      <Helmet>
        <title>ED3N - User Management</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">User Management</h1>
      <div className="userManagementActions">
        <Button
          className="primaryButton actionButton"
          text={<><PersonAddIcon fontSize="small" style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />Add New User</>}
          onClick={openAddModal}
        />
      </div>
           <div className="tableFilterGrid">
             <TableFilterConfig 
               rows={rows}
               onFilteredDataChange={(filtered) => {
                 setTableData(filtered);
                 setPage(0);
               }}
               filterConfig={filterConfig}
             />
           </div>
  
         {loading ? (
         <Loading isLoading={loading} />
      ) : (
      <div>
      <StyledPaper>       
        <TableContainer>
          <StyledTable 
            aria-label="user management table" >
            <MuiTableHeader 
              headCells={columns} 
              order={order}  
              orderBy={orderBy} 
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {stableSort(tableData, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                  <StyledTableRow tabIndex={-1} key={row.email || index}> 
                    <StyledTableCell style={{width: '25%'}}>{row.email}</StyledTableCell>
                    <StyledTableCell>{row.fullName}</StyledTableCell>
                    <StyledTableCell>{row.programId}</StyledTableCell>
                    <StyledTableCell>{row.roleName}</StyledTableCell>
                    <StyledTableCell>{row.isActive ? 'Active' : 'Inactive'}</StyledTableCell>
                    <StyledTableCell className="userActionsCell">
                        {row.isActive 
                        ? <><Button onClick={() => openModifyModal(row)} text="Modify" /> <Button onClick={() => openUpdateStatusModal(row)} text="Delete" /></>
                        : <Button onClick={() => openUpdateStatusModal(row)} text="Reactivate" />}
                    </StyledTableCell>
                  </StyledTableRow>
                  );
                })
              }
            </TableBody>
          </StyledTable>
        </TableContainer>
        <MuiTablePagination 
          rowsPerPage={rowsPerPage}
          page={page}
          count={tableData.length}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage} />
      </StyledPaper>
      
      <AddNewUserModal
        isOpen={modalIsOpen}
        onClose={closeUserModal}
        onSubmit={handleSaveUser}
        user={selectedUser}
        programOptions={programs}
        defaultProgramId={!isSysAdmin ? defaultProgramId : null}
        roleOptions={roles}/>
      
      <ConfirmationModal 
        title={selectedUser?.isActive ? "Delete User" : "Reactivate User"}
        message={confirmationMessage}
        onCancel={closeConfirmationModal}
        onConfirmation={updateUserStatus}
        onClose={closeConfirmationModal}
        isOpen={confirmationModalIsOpen}>
         {warningMessage && (
        <div className="exclamation">
          {warningMessage}
        </div>
      )}
      </ConfirmationModal>
        </div>
      )}

      <SuccessMessageModal 
        modalIsOpen={isSuccessModalOpen}
        closeModal={() => setIsSuccessModalOpen(false)}
        message={successMessage}/>
    </div>
  );
};

export default UserManagement;